import React, { useState, useEffect } from 'react';
import { RefreshCw, CircleDot, Search, Plus, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Card, LoadingState, EmptyState, Modal, Input } from '@/components/common';
import {
  getAdminStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
  type StatusData,
  type StatusFormData,
} from '@/services/nomenclatoare.service';

// Culori predefinite pentru statusuri
const PRESET_COLORS = [
  '#1A67A3', // Albastru
  '#22c55e', // Verde
  '#eab308', // Galben
  '#f97316', // Portocaliu
  '#ef4444', // Roșu
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#6b7280', // Gri
];

export function StatusesPage() {
  const [statuses, setStatuses] = useState<StatusData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StatusData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<StatusFormData>({
    name: '',
    color: '#1A67A3',
    isClosed: false,
    isDefault: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Delete state
  const [statusToDelete, setStatusToDelete] = useState<StatusData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadStatuses = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getAdminStatuses();
      setStatuses(data);
    } catch (err) {
      console.error('Failed to load statuses:', err);
      setError('Eroare la încărcarea statusurilor');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  const filteredStatuses = statuses.filter((status) => {
    const query = searchQuery.toLowerCase();
    return status.name.toLowerCase().includes(query);
  });

  const openCreateModal = () => {
    setEditingStatus(null);
    setFormData({
      name: '',
      color: '#1A67A3',
      isClosed: false,
      isDefault: false,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (status: StatusData) => {
    setEditingStatus(status);
    setFormData({
      name: status.name,
      color: status.color,
      isClosed: status.isClosed,
      isDefault: status.isDefault,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStatus(null);
    setFormData({
      name: '',
      color: '#1A67A3',
      isClosed: false,
      isDefault: false,
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Numele statusului este obligatoriu';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (editingStatus) {
        await updateStatus(editingStatus.id, formData);
        toast.success('Status actualizat cu succes!');
      } else {
        await createStatus(formData);
        toast.success('Status creat cu succes!');
      }

      closeModal();
      loadStatuses();
    } catch (err: unknown) {
      console.error('Failed to save status:', err);
      const errorMessage = (err as { message?: string })?.message || 'Eroare la salvarea statusului';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!statusToDelete) return;

    setIsDeleting(true);
    try {
      await deleteStatus(statusToDelete.id);
      toast.success('Status șters cu succes!');
      setStatusToDelete(null);
      loadStatuses();
    } catch (err: unknown) {
      console.error('Failed to delete status:', err);
      const errorMessage = (err as { message?: string })?.message || 'Eroare la ștergerea statusului';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingState message="Se încarcă statusurile..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-green-500 text-white">
            <CircleDot className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Status Activitate</h1>
            <p className="text-gray-600 mt-1">Administrare statusuri pentru activități</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredStatuses.length} statusuri
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadStatuses}
          >
            Reîncarcă
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={openCreateModal}
          >
            Adaugă status
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută statusuri..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filteredStatuses.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="list"
              title="Nu există statusuri"
              description={
                searchQuery
                  ? "Nu s-au găsit statusuri care să corespundă căutării."
                  : 'Nu există statusuri în sistem.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Poziție
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nume status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Culoare
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Tip
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStatuses.map((status) => (
                  <tr key={status.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">{status.position}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="font-medium text-gray-900">{status.name}</span>
                        {status.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            Implicit
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-600 font-mono">
                          {status.color || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {status.isClosed ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Închis
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Deschis
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Edit2 className="w-4 h-4" />}
                          onClick={() => openEditModal(status)}
                        >
                          Editează
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="w-4 h-4" />}
                          onClick={() => setStatusToDelete(status)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Șterge
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal pentru creare/editare */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingStatus ? 'Editare status' : 'Adaugă status nou'}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
              Anulează
            </Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={isSaving}>
              {editingStatus ? 'Salvează modificările' : 'Creează status'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nume status"
            type="text"
            placeholder="Ex: În lucru"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Culoare</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    formData.color === color
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="#1A67A3"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isClosed}
                onChange={(e) => setFormData({ ...formData, isClosed: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Status închis</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Status implicit</span>
            </label>
          </div>
        </form>
      </Modal>

      {/* Modal confirmare ștergere */}
      <Modal
        isOpen={!!statusToDelete}
        onClose={() => setStatusToDelete(null)}
        title="Confirmare ștergere"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setStatusToDelete(null)}
              disabled={isDeleting}
            >
              Anulează
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Șterge
            </Button>
          </div>
        }
      >
        <p className="text-gray-600">
          Sigur doriți să ștergeți statusul <strong>"{statusToDelete?.name}"</strong>?
        </p>
        <p className="text-sm text-red-600 mt-2">
          Această acțiune nu poate fi anulată.
        </p>
      </Modal>
    </div>
  );
}
